import { useEffect, useRef, useState } from 'react';

/**
 * State that is also kept in the browser, so a half-filled form survives a reload or leaving the page.
 * Saved straight away on every change.
 */
export function useDraft<T>(key: string, initial: () => T, valid: (v: unknown) => v is T): [T, (v: T) => void, () => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (valid(parsed)) return parsed;
      }
    } catch {
      /* fall through to the starting value */
    }
    return initial();
  });
  const keyRef = useRef(key);
  useEffect(() => {
    keyRef.current = key;
  }, [key]);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage may be blocked; the form still works */
    }
  }, [key, value]);

  const clear = () => {
    try {
      localStorage.removeItem(keyRef.current);
    } catch {
      /* ignore */
    }
    setValue(initial());
  };
  return [value, setValue, clear];
}
